/**
 * Footer Component
 * 
 * A reusable footer component for displaying copyright information throughout the application.
 * Supports two layout modes to accommodate different UI contexts.
 * 
 * Layout modes:
 * - Horizontal: Standard footer at the bottom of the page (default)
 *   Used in most screens like login, forms, etc.
 * 
 * - Vertical: Footer displayed on the right side of the screen
 *   Used in landscape-oriented screens like signature capture where the device is rotated
 *   to maximize UI space. Text is displayed vertically using writing-mode.
 * 
 * All styling is done with inline styles as per project requirements.
 */
import React from 'react';

export type FooterLayout = 'horizontal' | 'vertical';

export interface FooterProps {
  /** Copyright text */
  text?: string;
  /** Layout: horizontal (bottom) or vertical (right side, e.g. signature UI in landscape) */
  layout?: FooterLayout;
}

// Default copyright text for the company
const DEFAULT_COPYRIGHT = '© 2026 株式会社西行土木. All Rights Reserved';

export default function Footer({ text = DEFAULT_COPYRIGHT, layout = 'horizontal' }: FooterProps) {
  // Base styles for horizontal footer (standard bottom placement)
  const base: React.CSSProperties = {
    fontSize: '0.75rem',
    color: '#9a9a9a',
    margin: 0,
  };

  // Vertical layout: for landscape/signature screens where footer is on the right side
  if (layout === 'vertical') {
    // Styles specific to vertical footer layout
    // Uses sideways-lr writing mode to display text vertically along the right edge
    const verticalStyles: React.CSSProperties = {
      textAlign: 'center',
      fontSize: 10,
      color: '#888888',
      padding: '6px 0',
      writingMode: 'sideways-lr',
      margin: 0,
    };
    return (
      <footer
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
        }}
        aria-label="Copyright"
      >
        <p style={verticalStyles}>{text}</p>
      </footer>
    );
  }

  // Horizontal layout: standard footer at the bottom (default)
  return (
    <footer style={{ marginTop: 40, textAlign: 'center' }} aria-label="Copyright">
      <p style={base}>{text}</p>
    </footer>
  );
}
